import React, { createContext, useContext, useMemo } from 'react'

/**
 * Context that carries the `optimisticPaint` flag for the CLP filter/subcollection
 * responsive-interaction optimisation (Statsig gate `clp_responsive_filters`).
 *
 * Why page-level gate read:
 *   `quince-site-common` and `quince-components` must not read Statsig directly —
 *   they are shared packages consumed by many pages, some of which have no Statsig
 *   provider mounted. The gate is therefore read at the page (or widget) level and
 *   pushed down through this provider so leaf components can opt-in to the
 *   optimistic-paint pattern without prop-drilling.
 *
 * Why default false:
 *   Provider-less renders (e.g. Storybook, legacy pages, unit tests that don't wrap
 *   the tree) default to false, which is byte-for-byte identical to the pre-gate
 *   synchronous behavior. This preserves the inert legacy path as the safe default.
 */

interface ResponsiveInteractionsContextValue {
  optimisticPaint: boolean
}

const ResponsiveInteractionsContext = createContext<ResponsiveInteractionsContextValue>({
  optimisticPaint: false,
})

// @i18n-ignore
ResponsiveInteractionsContext.displayName = 'Responsive Interactions Context'

export interface ResponsiveInteractionsProviderProps {
  /**
   * Set to the value of the Statsig `clp_responsive_filters` gate, read at the page
   * or widget level. Consumers read it via `useOptimisticPaint()`.
   */
  optimisticPaint: boolean
  children: React.ReactNode
}

/**
 * Mount at the gate-read site (page component or widget). Wrap only the subtree
 * that contains filter/subcollection-card consumers — there is no performance cost
 * to mounting it higher, but keeping it close to the gate read makes intent clear.
 */
export function ResponsiveInteractionsProvider({ optimisticPaint, children }: ResponsiveInteractionsProviderProps) {
  const value = useMemo(() => ({ optimisticPaint }), [optimisticPaint])

  return <ResponsiveInteractionsContext.Provider value={value}>{children}</ResponsiveInteractionsContext.Provider>
}

/**
 * Returns the `optimisticPaint` flag from the nearest
 * `ResponsiveInteractionsProvider`. Falls back to `false` when no provider is
 * mounted — i.e. legacy pages and provider-less renders stay inert by default.
 */
export function useOptimisticPaint(): boolean {
  return useContext(ResponsiveInteractionsContext).optimisticPaint
}
